import Area from '@/components/Area';
import { GizmoHelper, GizmoViewport, MapControls, OrbitControls } from '@react-three/drei';
import { Canvas, ThreeEvent } from '@react-three/fiber';
import * as React from 'react';
import * as THREE from 'three';

type State = 'wall' | 'cabinet';

type CabinetSegmentProps = {
  length: number;
  height?: number;
  depth?: number;
  color?: number;
} & Omit<React.ComponentProps<'group'>, 'children'>;

function CabinetSegment(props: CabinetSegmentProps) {
  const { length, height = 96, depth = 24, color = 0xff0000, ...groupProps } = props;
  return (
    <group {...groupProps}>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            array={new Float32Array([0, 0, 0, length, 0, 0])}
            count={2}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={0x0000ff} />
      </line>
      <mesh position={[length / 2, height / 2, depth / 2]}>
        <boxGeometry args={[length, height, depth]} />
        <meshStandardMaterial color={color} wireframe />
      </mesh>
    </group>
  );
}

type WallProps = {
  length: number;
  height?: number;
  thickness?: number;
} & React.ComponentProps<'group'>;

function Wall(props: WallProps) {
  const { length, height = 10, thickness = 2, ...rest } = props;
  return (
    <group {...rest}>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            array={new Float32Array([0, 0, 0, length, 0, 0])}
            count={2}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={0xff0000} />
      </line>
      <mesh
        position={[length / 2, 0, 0]}
        // position={[length / 2, height / 2, 0]}
      >
        <boxGeometry args={[length, thickness, height]} />
        <meshStandardMaterial color={0xaaaaaa} side={THREE.DoubleSide} transparent opacity={0.5} />
      </mesh>
    </group>
  );
}

const CABINET_DEPTH = 10; // everything in inches
const CABINET_SEGMENT_HEIGHT = 5; // i.e. both wall & base cabinets -> group should extend to ceiling
const SNAPPING_DISTANCE = 4;

function getFloorIntersection(e: ThreeEvent<PointerEvent>) {
  const [intersection] = e.intersections;
  return intersection ? intersection.point : undefined;
}

type KitchenDesignerProps = {
  el: HTMLElement;
  state: State;
  setState: React.Dispatch<React.SetStateAction<State | undefined>>;
  wallPoints: THREE.Vector3[];
  setWallPoints: React.Dispatch<React.SetStateAction<THREE.Vector3[]>>;
  cabinetPoints: THREE.Vector3[];
  setCabinetPoints: React.Dispatch<React.SetStateAction<THREE.Vector3[]>>;
};

function KitchenDesigner(props: KitchenDesignerProps) {
  const { state, wallPoints, setWallPoints, cabinetPoints, setCabinetPoints } = props;
  const wallLinePoints = React.useMemo(() => {
    const pts: { start: THREE.Vector3; end: THREE.Vector3 }[] = [];
    for (let i = 1; i < wallPoints.length; i++) {
      const [start, end] = wallPoints.slice(i - 1, i + 1);
      pts.push({ start, end });
    }
    return pts;
  }, [wallPoints]);
  const cabinetLinePoints = React.useMemo(() => {
    const pts: { start: THREE.Vector3; end: THREE.Vector3 }[] = [];
    for (let i = 1; i < cabinetPoints.length; i++) {
      const [start, end] = cabinetPoints.slice(i - 1, i + 1);
      pts.push({ start, end });
    }
    return pts;
  }, [cabinetPoints]);

  const refMoving = React.useRef<THREE.Vector3 | null>(null);
  const bufferGeometryWalls = React.useMemo(() => new THREE.BufferGeometry(), []);
  const bufferGeometryCabinets = React.useMemo(() => new THREE.BufferGeometry(), []);

  const getSnappedPoint = React.useCallback(
    (point: THREE.Vector3) => {
      for (const wallPoint of wallPoints) {
        if (point.distanceTo(wallPoint) < SNAPPING_DISTANCE) {
          console.log('snapped!', point, wallPoint);
          return wallPoint;
        }
      }

      // if (wallPoints.length === 0) return point;
      // const lastPoint = wallPoints[wallPoints.length - 1];
      // const direction = new THREE.Vector3().subVectors(point, lastPoint);
      // const angle = Math.atan2(direction.y, direction.x);
      // const length = direction.length();
      // const snappedAngle = Math.round(angle / (Math.PI / 12)) * (Math.PI / 12); // 15도 단위로 스냅
      // const snappedDirection = new THREE.Vector3(Math.cos(snappedAngle), Math.sin(snappedAngle), 0).multiplyScalar(
      //   length
      // );
      // return lastPoint.clone().add(snappedDirection);
      return point;
    },
    [wallPoints]
  );

  const handlePointerDown = React.useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (state === 'wall') {
        const point = getFloorIntersection(e);
        if (point) {
          setWallPoints((prev) => [...prev, point]);
        }
      }
      if (state === 'cabinet') {
        const point = getFloorIntersection(e);
        if (point) {
          const snappedPoint = getSnappedPoint(point);
          setCabinetPoints((prev) => [...prev, snappedPoint]);
        }
      }
    },
    [state]
  );

  const handlePointerMove = React.useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      refMoving.current = e.point;
      if (state === 'wall' && wallPoints.length) {
        const lastPoint = wallPoints[wallPoints.length - 1];
        bufferGeometryWalls.setFromPoints([lastPoint, e.point]);
      }
      if (state === 'cabinet' && cabinetPoints.length) {
        const lastPoint = cabinetPoints[cabinetPoints.length - 1];
        bufferGeometryCabinets.setFromPoints([lastPoint, e.point]);
      }
    },
    [state, wallPoints, bufferGeometryWalls]
  );

  return (
    <>
      <mesh position={[0, 0, 0.001]} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove}>
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial color={0xcccccc} side={THREE.DoubleSide} />
      </mesh>
      <gridHelper args={[100, 100]} rotation={[-Math.PI / 2, 0, 0]} />
      {wallLinePoints.map(({ start, end }, index) => {
        const direction = end.clone().sub(start);
        const length = direction.length();
        const angle = Math.atan2(direction.y, direction.x);
        return <Wall key={index} length={length} position={start} rotation={[0, 0, angle]} />;
      })}
      {cabinetLinePoints.map(({ start, end }, index) => {
        const direction = end.clone().sub(start);
        const length = direction.length();
        const angle = Math.atan2(direction.y, direction.x);
        return (
          <CabinetSegment
            key={index}
            length={length}
            position={start}
            rotation={[0, 0, angle]}
            depth={CABINET_DEPTH}
            height={CABINET_SEGMENT_HEIGHT}
          />
        );
      })}
      <line>
        <bufferGeometry attach="geometry" {...bufferGeometryWalls} />
        <lineBasicMaterial color="blue" />
      </line>
      <line>
        <bufferGeometry attach="geometry" {...bufferGeometryCabinets} />
        <lineBasicMaterial color="red" />
      </line>
    </>
  );
}

export type Props = {};

function App01(props: Props) {
  const {} = props;
  // useFrame(() => {
  //   //
  // });
  const [state, setState] = React.useState<State>();
  const [wallPoints, setWallPoints] = React.useState<THREE.Vector3[]>([]);
  const [cabinetPoints, setCabinetPoints] = React.useState<THREE.Vector3[]>([]);

  return (
    <>
      <Area css={{ position: 'relative' }}>
        {(el) => (
          <>
            <Canvas
              eventSource={el}
              camera={{
                position: [0, 0, 100],
                fov: 5,
                // lookAt: [0, 0, 0]
              }}
            >
              <ambientLight intensity={0.5} />
              <KitchenDesigner
                el={el}
                state={state}
                setState={setState}
                wallPoints={wallPoints}
                setWallPoints={setWallPoints}
                cabinetPoints={cabinetPoints}
                setCabinetPoints={setCabinetPoints}
              />
              <MapControls />
              <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
                <GizmoViewport axisColors={['red', 'green', 'blue']} labelColor="black" />
              </GizmoHelper>
            </Canvas>
          </>
        )}
      </Area>
      <div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setState(undefined);
            setWallPoints([]);
            setCabinetPoints([]);
          }}
        >
          Clear
        </button>
        <span> | </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setState('wall');
          }}
        >
          W (Wall){state === 'wall' ? ' - Active' : ''}
        </button>
        <span> | </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setState('cabinet');
          }}
        >
          C (Cabinet){state === 'cabinet' ? ' - Active' : ''}
        </button>
        <span> | </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setState(undefined);
          }}
        >
          Fin
        </button>
      </div>
    </>
  );
}

export default React.memo(App01);
